import React, { createContext, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../features/store';
import { 
  addToCart as addToCartAction,
  removeFromCart as removeFromCartAction,
  updateQuantity as updateQuantityAction,
  clearCart as clearCartAction,
  openCart as openCartAction,
  closeCart as closeCartAction,
  toggleCart as toggleCartAction
} from '../features/cart';
import { 
  selectCartItems,
  selectCartIsOpen,
  selectCartItemCount,
  selectCartTotalPrice,
  selectCartTotalSavings
} from '../features/cart/cartSelectors';
import { Product } from '../features/products/productModels';

interface CartContextType {
  cartItems: any[];
  isCartOpen: boolean;
  addToCart: (product: Product, quantity?: number) => void;
  removeFromCart: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  openCart: () => void;
  closeCart: () => void;
  getItemCount: () => number;
  getTotalPrice: () => number;
  getTotalSavings: () => number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: React.ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const dispatch: AppDispatch = useDispatch();
  const cartItems = useSelector(selectCartItems);
  const isCartOpen = useSelector(selectCartIsOpen);
  const itemCount = useSelector(selectCartItemCount);
  const totalPrice = useSelector(selectCartTotalPrice);
  const totalSavings = useSelector(selectCartTotalSavings);

  const addToCart = (product: Product, quantity: number = 1) => {
    dispatch(addToCartAction({ product, quantity }));
  };

  const removeFromCart = (productId: string) => {
    dispatch(removeFromCartAction({ productId }));
  };

  const updateQuantity = (productId: string, quantity: number) => {
    dispatch(updateQuantityAction({ productId, quantity }));
  };

  const clearCart = () => {
    dispatch(clearCartAction());
  };

  const openCart = () => {
    dispatch(openCartAction());
  };

  const closeCart = () => {
    dispatch(closeCartAction());
  };

  const getItemCount = () => itemCount;
  const getTotalPrice = () => totalPrice;
  const getTotalSavings = () => totalSavings;

  const value: CartContextType = {
    cartItems,
    isCartOpen,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    openCart,
    closeCart,
    getItemCount,
    getTotalPrice,
    getTotalSavings,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
